/**
 * CCFI Fabrication calculator constants — mirrors backend fabrication.py.
 * The backend does all the maths; these lists drive the UI form + labels.
 */
export const FAB_TYPES = [
  { id: "balustrade", label: "Balustrade" },
  { id: "staircase", label: "Staircase" },
  { id: "steel_frame", label: "Steel / Alloy Frame" },
  { id: "gate", label: "Gate" },
  { id: "slat_panel", label: "Slat Panels" },
  { id: "custom_panel", label: "Custom Panel" },
  { id: "glass", label: "Glass" },
];

export const FAB_TYPE_LABEL = Object.fromEntries(FAB_TYPES.map((t) => [t.id, t.label]));

export const FAB_MATERIALS = [
  { id: "steel", label: "Mild steel (RHS/SHS)" },
  { id: "aluminium", label: "Aluminium" },
  { id: "stainless", label: "Stainless 316" },
];

export const FAB_FINISHES = [
  { id: "powder_coat", label: "Powder coat" },
  { id: "hot_dip_galv", label: "Hot-dip galvanised" },
  { id: "painted", label: "2-pack painted" },
  { id: "anodised", label: "Anodised" },
  { id: "mill", label: "Mill / raw finish" },
  { id: "other", label: "Other" },
];

export const INFILL_TYPES = [
  { id: "vertical_bar", label: "Vertical bars" },
  { id: "glass", label: "Glass panels" },
  { id: "wire", label: "Tensioned wire" },
];

export const FRAME_ORIENTATIONS = [
  { id: "vertical", label: "Vertical members" },
  { id: "horizontal", label: "Horizontal members" },
  { id: "both", label: "Both (mesh grid)" },
];

export const GLASS_THICKNESSES = [
  { id: "10", label: "10mm toughened" },
  { id: "12", label: "12mm toughened" },
  { id: "15", label: "15mm toughened" },
];

// Gate infill options — mirrors the backend `infill_type` branches in _calc_gate.
export const GATE_INFILL_TYPES = [
  { id: "bar",   label: "Vertical bars" },
  { id: "slat",  label: "Horizontal slats" },
  { id: "mesh",  label: "Welded mesh" },
  { id: "sheet", label: "Sheet / plate" },
  { id: "laser", label: "Laser-cut screen" },
];

// Per-fab-type "design style" presets. The chosen style is recorded on every
// BOM line + summary + spec sheet so the workshop knows what to cut/weld.
// `member_default` is the default member cross-section (WxH mm) — feeds
// `member_width_mm` when the user first picks the style.
export const DESIGN_STYLES = {
  balustrade: [
    { id: "vertical_bar",   label: "Vertical bars",         member_default: [16, 16] },
    { id: "horizontal_bar", label: "Horizontal bars",       member_default: [40, 25] },
    { id: "cattle_rail",    label: "Cattle rail (3-rail)",  member_default: [50, 50] },
    { id: "glass_infill",   label: "Glass panels",          member_default: [50, 50] },
    { id: "wire_infill",    label: "Tensioned wire",        member_default: [50, 50] },
    { id: "laser_screen",   label: "Laser-cut screen",      member_default: [50, 50] },
  ],
  staircase: [
    { id: "closed_riser",   label: "Closed riser (solid)",     member_default: [100, 50] },
    { id: "open_riser",     label: "Open riser (visible)",     member_default: [100, 50] },
    { id: "spiral",         label: "Spiral / helical",          member_default: [100, 100] },
    { id: "straight",       label: "Straight run",              member_default: [100, 50] },
    { id: "landing",        label: "L-shape with landing",      member_default: [100, 50] },
  ],
  steel_frame: [
    { id: "vertical",       label: "Vertical members",          member_default: [40, 40] },
    { id: "horizontal",     label: "Horizontal members",        member_default: [40, 40] },
    { id: "both",           label: "Mesh grid (both directions)", member_default: [40, 40] },
    { id: "diagonal_brace", label: "Diagonal bracing",          member_default: [40, 40] },
    { id: "cross_hatch",    label: "Cross-hatch pattern",       member_default: [30, 30] },
  ],
  slat_panel: [
    { id: "horizontal_slat", label: "Horizontal slats",         member_default: [65, 16] },
    { id: "vertical_slat",   label: "Vertical battens",         member_default: [65, 16] },
    { id: "angled_slat",     label: "Angled / privacy louvre",  member_default: [65, 16] },
    { id: "mixed_gap",       label: "Mixed-width slats",        member_default: [65, 16] },
    { id: "two_tone",        label: "Two-tone (alternating)",   member_default: [65, 16] },
  ],
  custom_panel: [
    { id: "laser_cut",      label: "Laser-cut screen",           member_default: [50, 50] },
    { id: "batten_screen",  label: "Batten privacy screen",      member_default: [40, 40] },
    { id: "welded_mesh",    label: "Welded mesh panel",          member_default: [40, 40] },
    { id: "decorative_scroll", label: "Decorative scroll",       member_default: [30, 30] },
    { id: "perforated_sheet", label: "Perforated sheet infill",  member_default: [40, 40] },
    { id: "other",          label: "Other (see description)",    member_default: [40, 40] },
  ],
  glass: [
    { id: "frameless",       label: "Frameless (spigot)",       member_default: [50, 50] },
    { id: "semi_frameless",  label: "Semi-frameless",           member_default: [50, 50] },
    { id: "channel",         label: "Channel-set",              member_default: [65, 50] },
    { id: "standoff",        label: "Stand-off pin fixed",      member_default: [50, 50] },
  ],
  gate: [
    { id: "swing_single",    label: "Single-swing",             member_default: [50, 50] },
    { id: "swing_double",    label: "Double-swing",             member_default: [50, 50] },
    { id: "sliding",         label: "Sliding (on track)",       member_default: [65, 50] },
    { id: "bi_fold",         label: "Bi-fold",                  member_default: [50, 50] },
    { id: "cantilever",      label: "Cantilever (no track)",    member_default: [65, 65] },
    { id: "pedestrian",      label: "Pedestrian",               member_default: [40, 40] },
  ],
};

export const designStylesFor = (fabType) => DESIGN_STYLES[fabType] || [];

export const FAB_DEFAULTS = {
  balustrade: {
    fab_type: "balustrade", material: "steel", finish: "powder_coat", qty: 1,
    length_m: 6, height_mm: 1000, post_spacing_mm: 1200, infill_type: "vertical_bar",
    baluster_spacing_mm: 100, panel_width_mm: 1200, glass_thickness_mm: 10, has_handrail: true,
    design_style: "vertical_bar", member_width_mm: 50, member_depth_mm: 50, notes: "",
  },
  staircase: {
    fab_type: "staircase", material: "steel", finish: "hot_dip_galv", qty: 1,
    total_rise_mm: 2700, riser_height_mm: 180, tread_depth_mm: 255, stair_width_mm: 900,
    has_balustrade: true,
    design_style: "closed_riser", member_width_mm: 100, member_depth_mm: 50, notes: "",
  },
  steel_frame: {
    fab_type: "steel_frame", material: "steel", finish: "powder_coat", qty: 1,
    frame_width_mm: 2000, frame_height_mm: 2000, member_spacing_mm: 450,
    member_orientation: "vertical",
    design_style: "vertical", member_width_mm: 40, member_depth_mm: 40, notes: "",
  },
  slat_panel: {
    fab_type: "slat_panel", material: "aluminium", finish: "powder_coat", qty: 1,
    length_m: 6, height_mm: 1800, slat_height_mm: 65, slat_gap_mm: 10, post_spacing_mm: 2400,
    colour: "",
    design_style: "horizontal_slat", member_width_mm: 65, member_depth_mm: 16, notes: "",
  },
  custom_panel: {
    fab_type: "custom_panel", material: "aluminium", finish: "powder_coat", qty: 1,
    length_m: 3, height_mm: 1800, num_panels: 1, estimated_member_lm: 0, labour_hours: 0,
    colour: "", description: "",
    design_style: "laser_cut", member_width_mm: 50, member_depth_mm: 50, notes: "",
  },
  glass: {
    fab_type: "glass", material: "glass", finish: "mill", qty: 1,
    length_m: 8, height_mm: 1200, panel_width_mm: 1200, glass_thickness_mm: 12, has_handrail: false,
    design_style: "frameless", member_width_mm: 50, member_depth_mm: 50, notes: "",
  },
  gate: {
    fab_type: "gate", material: "steel", finish: "powder_coat", qty: 1,
    leaf_width_mm: 1200, leaf_height_mm: 1500, num_leaves: 1,
    gate_style: "swing_single", infill_type: "bar",
    baluster_spacing_mm: 100, slat_height_mm: 65, slat_gap_mm: 10,
    has_motor: false, has_drop_bolt: false, has_self_close: false, has_key_lock: true,
    design_style: "swing_single", member_width_mm: 50, member_depth_mm: 50, notes: "",
  },
};

export const fabDefault = (t) => ({ ...(FAB_DEFAULTS[t] || FAB_DEFAULTS.balustrade) });

/** Short human summary from an input object (no API call needed). */
export function fabInputSummary(fab) {
  if (!fab) return "Fabricated item";
  const t = fab.fab_type;
  if (t === "staircase") return `Staircase · ${fab.total_rise_mm || 0}mm rise · ${fab.stair_width_mm || 0}mm wide`;
  if (t === "balustrade") return `Balustrade · ${fab.length_m || 0}m × ${fab.height_mm || 0}mm · ${(fab.infill_type || "").replace("_", " ")}`;
  if (t === "steel_frame") return `Frame · ${fab.frame_width_mm || 0}×${fab.frame_height_mm || 0}mm`;
  if (t === "slat_panel") return `Slat panel · ${fab.length_m || 0}m × ${fab.height_mm || 0}mm · ${fab.slat_height_mm || 65}/${fab.slat_gap_mm || 10}mm`;
  if (t === "custom_panel") return `Custom panel · ${fab.num_panels || 1}× ${fab.length_m || 0}m × ${fab.height_mm || 0}mm`;
  if (t === "gate") return `${(fab.gate_style || "swing_single").replace("_", " ")} gate · ${fab.num_leaves || 1}× ${fab.leaf_width_mm || 0}×${fab.leaf_height_mm || 0}mm${fab.has_motor ? " · motorised" : ""}`;
  if (t === "glass") return `Glass · ${fab.length_m || 0}m × ${fab.height_mm || 0}mm`;
  return "Fabricated item";
}
